import { useState, useEffect } from 'react';

import { DownloadFormatSelector } from './download-format-selector';
import { DownloadTableModal } from './download-table';
import { DownloadWrittenModal } from './download-written';
import { StyleFormat, DownloadFormat, DocumentDownloadOptions } from '../../../../../../backend/src/types/types';
import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';
import { Icon } from '@/new/components/icon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/new/components/shadcn/dialog';

interface DownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  modalTitle: string;
  downloadTitle: string;
  isLoading: boolean;
  columnOptions: string[];
  relativityColumnOptions?: FilterField[];
  onDownloadTable: (params: {
    title: string;
    columns: string[];
    relativityColumns?: string[];
    downloadFormat: DownloadFormat;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }) => void;
  onDownloadWritten: (params: {
    title: string;
    downloadFormat: DownloadFormat;
    documentOptions: DocumentDownloadOptions;
    style?: string;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }) => void;
  displayOptions?: {
    showStyle?: boolean;
    showDocumentOptions?: boolean;
    showSourceDocumentsOption?: boolean;
  };
  showEmailNotification?: boolean;
}

export const DownloadDialog = ({
  isOpen,
  onClose,
  modalTitle,
  downloadTitle,
  isLoading,
  columnOptions,
  relativityColumnOptions,
  onDownloadTable,
  onDownloadWritten,
  displayOptions = {
    showStyle: true,
    showDocumentOptions: false,
    showSourceDocumentsOption: false,
  },
  showEmailNotification = false,
}: DownloadDialogProps) => {
  const [selectedFormat, setSelectedFormat] = useState<StyleFormat | null>(null);

  const handleDownloadTableWithClose = (params: {
    title: string;
    columns: string[];
    relativityColumns?: string[];
    downloadFormat: DownloadFormat;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }) => {
    onDownloadTable(params);
  };

  const handleDownloadWrittenWithClose = (params: {
    title: string;
    downloadFormat: DownloadFormat;
    documentOptions: DocumentDownloadOptions;
    style?: string;
    includeSourceDocuments?: boolean;
    orderByDate?: boolean;
  }) => {
    onDownloadWritten(params);
  };

  // Reset format when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
        </DialogHeader>
        {selectedFormat === null ? (
          <DownloadFormatSelector onSelectFormat={setSelectedFormat} />
        ) : selectedFormat === StyleFormat.Table ? (
          <DownloadTableModal
            columnOptions={columnOptions}
            relativityColumnOptions={relativityColumnOptions}
            downloadTitle={downloadTitle}
            loadingDownload={isLoading}
            handleDownload={handleDownloadTableWithClose}
            showSourceDocumentsOption={displayOptions.showSourceDocumentsOption}
            showEmailNotification={showEmailNotification}
          />
        ) : (
          <DownloadWrittenModal
            downloadTitle={downloadTitle}
            loadingDownload={isLoading}
            handleDownload={handleDownloadWrittenWithClose}
            showStyle={displayOptions.showStyle}
            showDocumentOptions={displayOptions.showDocumentOptions}
            showSourceDocumentsOption={displayOptions.showSourceDocumentsOption}
            showEmailNotification={showEmailNotification}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
